<template>
  <Teleport to="body">
    <div class="records-backdrop" @click.self="$emit('close')">
      <div class="records-panel" role="dialog" aria-label="Records">
        <h2>Records</h2>
        <p v-if="loading" class="sub">Loading your games…</p>
        <p v-else-if="error" class="error">{{ error }}</p>
        <p v-else-if="!stats.games" class="sub">No finished games yet - top out once and they start here.</p>
        <template v-else>
          <div class="tabs" role="tablist">
            <button
              v-for="t in tabs"
              :key="t.key"
              type="button"
              role="tab"
              :class="{ on: tab === t.key }"
              :aria-selected="tab === t.key"
              @click="tab = t.key"
            >{{ t.label }}</button>
          </div>

          <ol v-if="tab === 'best'" class="rows">
            <li v-for="g in shownBest" :key="g.id">
              <b>{{ g.score.toLocaleString() }}</b>
              <span>{{ g.players.join(', ') }}</span>
              <small>{{ g.lines }} lines · {{ when(g.at) }}</small>
            </li>
          </ol>
          <button
            v-if="tab === 'best' && !showAll && stats.best.length > TOP"
            type="button"
            class="more"
            @click="showAll = true"
          >Show all {{ stats.best.length }}</button>

          <ol v-if="tab === 'teams'" class="rows">
            <li v-if="!stats.teams.length" class="empty">Play with someone else to see teams here.</li>
            <li v-for="t in stats.teams" :key="t.key">
              <b>{{ t.best.toLocaleString() }}</b>
              <span>{{ t.names.join(' + ') }}</span>
              <small>{{ t.games }} game{{ t.games > 1 ? 's' : '' }} · average {{ t.average.toLocaleString() }}</small>
            </li>
          </ol>

          <ol v-if="tab === 'with'" class="rows">
            <li v-if="!stats.playedWith.length" class="empty">
              Nobody from another account yet - host an online game and they'll show up here.
            </li>
            <li v-for="p in stats.playedWith" :key="p.key">
              <b>{{ p.games }}</b>
              <span>{{ p.name }}</span>
              <small>last played {{ when(p.last) }}</small>
            </li>
          </ol>

          <ol v-if="tab === 'mine'" class="rows">
            <li v-for="m in stats.mine" :key="m.key">
              <b>{{ m.best.toLocaleString() }}</b>
              <span>{{ m.name }}</span>
              <small>{{ m.games }} game{{ m.games > 1 ? 's' : '' }} · {{ m.cleared }} blocks in cleared lines</small>
            </li>
          </ol>
          <p class="sub foot">{{ stats.games }} game{{ stats.games > 1 ? 's' : '' }} saved</p>
        </template>
        <div class="actions">
          <button type="button" class="primary" @click="$emit('close')">Done</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { loadGames } from '@/account/account.js';
import { computeStats } from '@/account/stats.js';

export default {
  name: 'RecordsPanel',
  props: { uid: { type: String, required: true } },
  emits: ['close'],
  data() {
    return {
      loading: true,
      error: '',
      stats: null,
      tab: 'best',
      showAll: false,
      TOP: 10,
      tabs: [
        { key: 'best', label: 'Best games' },
        { key: 'teams', label: 'Teams' },
        { key: 'with', label: 'Played with' },
        { key: 'mine', label: 'Your players' },
      ],
    };
  },
  computed: {
    shownBest() { return this.showAll ? this.stats.best : this.stats.best.slice(0, this.TOP); },
  },
  async mounted() {
    try {
      this.stats = computeStats(await loadGames(this.uid), this.uid);
    } catch {
      this.error = "Couldn't load your games - check the connection.";
    }
    this.loading = false;
  },
  methods: {
    when(at) {
      if (!at) return '';
      return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
  },
};
</script>

<style scoped>
.records-backdrop {
  position: fixed; inset: 0; z-index: 900; background: rgba(0, 0, 0, 0.6);
  display: grid; place-items: center; padding: 16px;
}
.records-panel {
  width: min(560px, 100%); max-height: 88vh; overflow-y: auto; background: #1a1d29;
  border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 16px; padding: 1.25rem; color: #eef0f6;
}
h2 { margin: 0 0 0.75rem; font-size: 1.4rem; }
.sub { opacity: 0.7; font-size: 0.9rem; }
.foot { margin: 0.75rem 0 0; font-size: 0.8rem; }
.error { color: #ff8f84; }
.tabs { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.75rem; }
.tabs button {
  background: transparent; color: #eef0f6; border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px; padding: 0.3rem 0.8rem; font-size: 0.85rem;
}
.tabs button.on { background: #eef0f6; color: #12141c; }
.rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.rows li {
  display: grid; grid-template-columns: 5.5rem 1fr; column-gap: 0.75rem; align-items: baseline;
  background: #12141c; border-radius: 10px; padding: 0.55rem 0.75rem;
}
.rows li.empty { display: block; opacity: 0.7; font-size: 0.9rem; }
.rows b { font-variant-numeric: tabular-nums; grid-row: span 2; font-size: 1.15rem; }
.rows span { font-weight: 600; overflow-wrap: anywhere; }
.rows small { opacity: 0.6; }
.more {
  display: block; margin: 0.6rem auto 0; background: transparent; color: #eef0f6;
  border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 999px; padding: 0.35rem 1rem; font-size: 0.85rem;
}
.actions { display: flex; justify-content: flex-end; margin-top: 1rem; }
</style>
